(() => {
  class MtFastQuickBuy extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abortController = new AbortController();
      this.syncNodes();
      this.request = null;
      this.trigger = null;
      this.productUrl = '';
      this.variants = [];
      this.selectedOptions = [];
      this.currentVariant = null;
      this.inFlight = false;
      this.closeTimer = null;

      const signal = this.abortController.signal;
      this.addEventListener('click', (event) => this.onClick(event), { signal, capture: true });
      this.addEventListener('change', (event) => this.onChange(event), { signal });
      this.addEventListener('submit', (event) => this.onSubmit(event), { signal });
      document.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    }

    disconnectedCallback() {
      this.abortRequest();
      this.abortController?.abort();
      window.clearTimeout(this.closeTimer);
      this.dialog?.classList.remove('is-open');
      if (this.dialog) this.dialog.hidden = true;
      this.trigger?.setAttribute('aria-expanded', 'false');
      this.contentHost?.replaceChildren();
      this.trigger = null;
      this.closeTimer = null;
      this.initialized = false;
    }

    onClick(event) {
      const trigger = event.target.closest('[data-mt-fast-quick-buy-trigger]');
      if (trigger && this.contains(trigger)) {
        event.preventDefault();
        event.stopPropagation();
        if (!trigger.disabled) this.open(trigger);
        return;
      }

      if (event.target.closest('[data-mt-fast-quick-buy-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      const material = event.target.closest('[data-mt-fast-quick-buy-material]');
      if (material && !material.disabled) {
        this.selectMaterial(material);
        return;
      }

      const quantityButton = event.target.closest('[data-mt-fast-quick-buy-quantity-change]');
      if (quantityButton) this.changeQuantity(Number(quantityButton.dataset.mtFastQuickBuyQuantityChange));
    }

    onChange(event) {
      if (event.target.matches('[data-mt-fast-quick-buy-option]')) this.selectOption(event.target);
      if (event.target.matches('[data-mt-fast-quick-buy-quantity-input]')) this.normalizeQuantity();
    }

    onKeydown(event) {
      if (!this.dialog || this.dialog.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key === 'Tab') this.trapFocus(event);
    }

    onSubmit(event) {
      const form = event.target.closest('[data-mt-fast-quick-buy-content] form');
      const button = event.submitter;
      if (
        !form ||
        !button?.matches('[data-mt-fast-quick-buy-add], [data-mt-fast-quick-buy-buy-now]')
      )
        return;
      event.preventDefault();
      if (button.matches('[data-mt-fast-quick-buy-buy-now]')) {
        this.buyNow(button);
        return;
      }
      this.addToCart(form, button);
    }

    async open(trigger) {
      this.syncNodes();
      if (!this.dialog || !this.drawer || !this.contentHost) return;
      const productUrl = trigger.dataset.productUrl;
      const variantId = trigger.dataset.variantId;
      if (!productUrl || !variantId) return;

      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
      this.trigger?.setAttribute('aria-expanded', 'false');
      this.trigger = trigger;
      this.productUrl = productUrl;
      trigger.setAttribute('aria-expanded', 'true');
      this.dialog.hidden = false;
      this.setLoading();
      window.requestAnimationFrame(() => {
        this.dialog.classList.add('is-open');
        this.drawer.focus({ preventScroll: true });
      });

      try {
        await this.load(productUrl, variantId);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.showLoadError(error instanceof Error ? error.message : this.dataset.stringError);
      }
    }

    syncNodes() {
      this.dialog = this.querySelector('[data-mt-fast-quick-buy-dialog]');
      this.drawer = this.querySelector('[data-mt-fast-quick-buy-drawer]');
      this.contentHost = this.querySelector('[data-mt-fast-quick-buy-content-host]');
    }

    async load(productUrl, variantId) {
      this.abortRequest();
      const controller = new AbortController();
      this.request = controller;
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('view', 'fast-quick-buy');
      url.searchParams.set('variant', variantId);
      const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) throw new Error(this.dataset.stringError || this.dataset.stringUnavailable);
      const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = responseDocument.querySelector('[data-mt-fast-quick-buy-content]');
      if (!content) throw new Error(this.dataset.stringError || this.dataset.stringUnavailable);
      if (controller.signal.aborted) return;

      this.contentHost.replaceChildren(document.importNode(content, true));
      this.contentHost.classList.remove('is-loading');
      this.contentHost.setAttribute('aria-busy', 'false');
      this.initializeProduct(variantId);
      if (this.request === controller) this.request = null;
    }

    initializeProduct(variantId) {
      const root = this.contentHost.querySelector('[data-mt-fast-quick-buy-content]');
      this.productRoot = root;
      this.productUrl = root?.dataset.productUrl || this.productUrl;
      this.colorIndex = this.parseIndex(root?.dataset.colorOptionIndex);
      this.fabricIndex = this.parseIndex(root?.dataset.fabricOptionIndex);
      try {
        this.variants = JSON.parse(root?.querySelector('[data-mt-fast-quick-buy-variants]')?.textContent || '[]');
      } catch (_) {
        this.variants = [];
      }
      this.currentVariant =
        this.variants.find((variant) => String(variant.id) === String(variantId)) ||
        this.variants.find((variant) => variant.available) ||
        this.variants[0] ||
        null;
      if (!this.currentVariant) {
        this.setUnavailable();
        return;
      }
      this.selectedOptions = [...this.currentVariant.options];
      this.syncVariant(this.currentVariant, { resetQuantity: true });
    }

    selectOption(input) {
      const index = Number(input.dataset.optionIndex);
      if (!Number.isInteger(index)) return;
      const nextOptions = [...this.selectedOptions];
      nextOptions[index] = input.value;
      const variant = this.findVariant(nextOptions, true) || this.findVariant(nextOptions, false);
      if (!variant) {
        this.setUnavailable();
        return;
      }
      this.syncVariant(variant, { resetQuantity: false });
    }

    selectMaterial(button) {
      if (this.fabricIndex < 0 || this.colorIndex < 0) return;
      const nextOptions = [...this.selectedOptions];
      nextOptions[this.fabricIndex] = button.dataset.fabricValue || '';
      nextOptions[this.colorIndex] = button.dataset.colorValue || '';
      const variant = this.findVariant(nextOptions, true) || this.findVariant(nextOptions, false);
      if (!variant) {
        this.setUnavailable();
        return;
      }
      this.syncVariant(variant, { resetQuantity: false });
    }

    findVariant(options, availableOnly) {
      return (
        this.variants.find(
          (variant) =>
            (!availableOnly || variant.available) &&
            variant.options.length === options.length &&
            variant.options.every((value, index) => value === options[index])
        ) || null
      );
    }

    syncVariant(variant, { resetQuantity = false } = {}) {
      this.currentVariant = variant;
      this.selectedOptions = [...variant.options];
      const root = this.productRoot;
      if (!root) return;

      root.querySelectorAll('[data-mt-fast-quick-buy-option]').forEach((input) => {
        const index = Number(input.dataset.optionIndex);
        input.checked = this.selectedOptions[index] === input.value;
      });
      root.querySelectorAll('[data-mt-fast-quick-buy-option-group]').forEach((group) => {
        const index = Number(group.dataset.optionIndex);
        const current = group.querySelector('[data-mt-fast-quick-buy-option-current]');
        if (current) current.textContent = this.selectedOptions[index] || '';
      });
      root.querySelectorAll('[data-mt-fast-quick-buy-material]').forEach((button) => {
        const active =
          this.selectedOptions[this.fabricIndex] === button.dataset.fabricValue &&
          this.selectedOptions[this.colorIndex] === button.dataset.colorValue;
        button.classList.toggle('is-selected', active);
        button.setAttribute('aria-checked', String(active));
        button.tabIndex = active ? 0 : -1;
      });
      const materialCurrent = root.querySelector('[data-mt-fast-quick-buy-material-current]');
      if (materialCurrent && this.colorIndex >= 0 && this.fabricIndex >= 0) {
        materialCurrent.textContent = `${this.selectedOptions[this.colorIndex]} · ${this.selectedOptions[this.fabricIndex]}`;
      }

      const variantInput = root.querySelector('[data-mt-fast-quick-buy-variant-id]');
      if (variantInput) variantInput.value = variant.id;
      const price = root.querySelector('[data-mt-fast-quick-buy-price-current]');
      const compare = root.querySelector('[data-mt-fast-quick-buy-price-compare]');
      if (price) price.textContent = variant.price || '';
      if (compare) {
        compare.hidden = !variant.comparePrice;
        compare.textContent = variant.comparePrice || '';
      }
      const mediaTemplate = root.querySelector(
        `[data-mt-fast-quick-buy-media-template="${CSS.escape(String(variant.id))}"]`
      );
      const media = root.querySelector('[data-mt-fast-quick-buy-media]');
      if (media && mediaTemplate) media.replaceChildren(mediaTemplate.content.cloneNode(true));
      const productUrl = new URL(this.productUrl, window.location.origin);
      productUrl.searchParams.set('variant', variant.id);
      root.querySelectorAll('[data-mt-fast-quick-buy-product-link]').forEach((link) => {
        link.href = `${productUrl.pathname}${productUrl.search}${productUrl.hash}`;
      });

      this.syncQuantityRule(variant, resetQuantity);
      this.updateAvailability();
      this.setPurchaseState(Boolean(variant.available));
      this.setStatus('');
    }

    updateAvailability() {
      const root = this.productRoot;
      if (!root) return;
      root.querySelectorAll('[data-mt-fast-quick-buy-option]').forEach((input) => {
        const index = Number(input.dataset.optionIndex);
        const available = this.variants.some(
          (variant) =>
            variant.available &&
            variant.options[index] === input.value &&
            variant.options.every(
              (value, optionIndex) => optionIndex === index || value === this.selectedOptions[optionIndex]
            )
        );
        input.disabled = !available;
        input.setAttribute('aria-disabled', String(!available));
      });
      root.querySelectorAll('[data-mt-fast-quick-buy-material]').forEach((button) => {
        const available = this.variants.some(
          (variant) =>
            variant.available &&
            variant.options[this.fabricIndex] === button.dataset.fabricValue &&
            variant.options[this.colorIndex] === button.dataset.colorValue &&
            variant.options.every(
              (value, optionIndex) =>
                optionIndex === this.fabricIndex ||
                optionIndex === this.colorIndex ||
                value === this.selectedOptions[optionIndex]
            )
        );
        button.disabled = !available;
        button.setAttribute('aria-disabled', String(!available));
      });
    }

    syncQuantityRule(variant, resetQuantity) {
      const input = this.productRoot?.querySelector('[data-mt-fast-quick-buy-quantity-input]');
      if (!input) return;
      const rule = variant.quantityRule || {};
      input.min = String(Number(rule.min) || 1);
      input.step = String(Number(rule.increment) || 1);
      if (rule.max === null || rule.max === undefined || rule.max === '') input.removeAttribute('max');
      else input.max = String(rule.max);
      if (resetQuantity) input.value = input.min;
      this.normalizeQuantity();
    }

    normalizeQuantity() {
      const input = this.productRoot?.querySelector('[data-mt-fast-quick-buy-quantity-input]');
      if (!input) return 1;
      const minimum = Number(input.min) || 1;
      const increment = Number(input.step) || 1;
      const maximum = input.max === '' ? Number.POSITIVE_INFINITY : Number(input.max);
      const requested = Number(input.value);
      const steps = Number.isFinite(requested) ? Math.round((requested - minimum) / increment) : 0;
      const value = Math.min(maximum, Math.max(minimum, minimum + Math.max(0, steps) * increment));
      input.value = String(value);
      this.productRoot.querySelectorAll('[data-mt-fast-quick-buy-quantity-change]').forEach((button) => {
        const direction = Number(button.dataset.mtFastQuickBuyQuantityChange);
        button.disabled = direction < 0 ? value <= minimum : Number.isFinite(maximum) && value >= maximum;
      });
      return value;
    }

    changeQuantity(direction) {
      if (!Number.isFinite(direction) || direction === 0) return;
      const input = this.productRoot?.querySelector('[data-mt-fast-quick-buy-quantity-input]');
      if (!input) return;
      const increment = Number(input.step) || 1;
      input.value = String((Number(input.value) || Number(input.min) || 1) + direction * increment);
      this.normalizeQuantity();
    }

    setPurchaseState(available) {
      const addButton = this.productRoot?.querySelector('[data-mt-fast-quick-buy-add]');
      const buyNowButton = this.productRoot?.querySelector('[data-mt-fast-quick-buy-buy-now]');
      const addLabel = addButton?.querySelector('[data-mt-fast-quick-buy-add-label]');
      const buyNowLabel = buyNowButton?.querySelector('[data-mt-fast-quick-buy-buy-now-label]');
      [addButton, buyNowButton].forEach((button) => {
        if (button) button.disabled = this.inFlight || !available;
      });
      if (addLabel) addLabel.textContent = available ? this.dataset.stringAdd : this.dataset.stringSoldOut;
      if (buyNowLabel) buyNowLabel.textContent = available ? this.dataset.stringBuyNow : this.dataset.stringSoldOut;
    }

    setUnavailable() {
      this.currentVariant = null;
      const addButton = this.productRoot?.querySelector('[data-mt-fast-quick-buy-add]');
      const buyNowButton = this.productRoot?.querySelector('[data-mt-fast-quick-buy-buy-now]');
      const addLabel = addButton?.querySelector('[data-mt-fast-quick-buy-add-label]');
      const buyNowLabel = buyNowButton?.querySelector('[data-mt-fast-quick-buy-buy-now-label]');
      [addButton, buyNowButton].forEach((button) => {
        if (button) button.disabled = true;
      });
      if (addLabel) addLabel.textContent = this.dataset.stringUnavailable;
      if (buyNowLabel) buyNowLabel.textContent = this.dataset.stringUnavailable;
    }

    buyNow(button) {
      if (this.inFlight || button.disabled || !this.currentVariant?.available) return;
      const quantity = this.normalizeQuantity();
      const variantId = encodeURIComponent(String(this.currentVariant.id));
      const cartUrl = (this.dataset.cartUrl || '/cart').replace(/\/+$/, '');

      this.inFlight = true;
      this.setPurchaseState(true);
      window.location.assign(`${cartUrl}/${variantId}:${quantity}`);
    }

    async addToCart(form, button) {
      if (this.inFlight || button.disabled || !this.currentVariant?.available) return;
      this.inFlight = true;
      this.setPurchaseState(true);
      this.setStatus('');
      try {
        const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        const payload = await this.parseResponse(response);
        if (!response.ok) throw new Error(payload.description || payload.message || this.dataset.stringError);
        this.close({ restoreFocus: false, immediate: true });
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: { source: 'product', origin: 'mt-fast-quick-buy' },
          })
        );
      } catch (error) {
        this.setStatus(error instanceof Error ? error.message : this.dataset.stringError, true);
      } finally {
        this.inFlight = false;
        if (this.productRoot?.isConnected) this.setPurchaseState(Boolean(this.currentVariant?.available));
      }
    }

    async parseResponse(response) {
      const text = await response.text();
      if (!text) return {};
      try {
        return JSON.parse(text);
      } catch (_) {
        return {};
      }
    }

    setLoading() {
      this.abortRequest();
      this.variants = [];
      this.selectedOptions = [];
      this.currentVariant = null;
      this.contentHost.classList.add('is-loading');
      this.contentHost.setAttribute('aria-busy', 'true');
      const loading = document.createElement('p');
      loading.className = 'mt-fast-quick-buy__loading';
      loading.textContent = this.dataset.stringLoading || '';
      this.contentHost.replaceChildren(loading);
    }

    showLoadError(message) {
      this.contentHost.classList.remove('is-loading');
      this.contentHost.setAttribute('aria-busy', 'false');
      const status = document.createElement('p');
      status.className = 'mt-fast-quick-buy__loading is-error';
      status.setAttribute('role', 'status');
      status.textContent = message || this.dataset.stringError || this.dataset.stringUnavailable;
      this.contentHost.replaceChildren(status);
    }

    setStatus(message, isError = false) {
      const status = this.productRoot?.querySelector('[data-mt-fast-quick-buy-status]');
      if (!status) return;
      status.textContent = message || '';
      status.hidden = !message;
      status.classList.toggle('is-error', Boolean(message && isError));
    }

    close({ restoreFocus = true, immediate = false } = {}) {
      if (!this.dialog || this.dialog.hidden) return;
      this.abortRequest();
      window.clearTimeout(this.closeTimer);
      const trigger = this.trigger;
      this.dialog.classList.remove('is-open');
      trigger?.setAttribute('aria-expanded', 'false');
      const finish = () => {
        this.dialog.hidden = true;
        this.contentHost?.replaceChildren();
        if (restoreFocus) window.requestAnimationFrame(() => trigger?.focus({ preventScroll: true }));
        if (this.trigger === trigger) this.trigger = null;
        this.closeTimer = null;
      };
      if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
      else this.closeTimer = window.setTimeout(finish, 260);
    }

    trapFocus(event) {
      const focusable = Array.from(
        this.drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusable.length) {
        event.preventDefault();
        this.drawer.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!this.drawer.contains(document.activeElement) || document.activeElement === this.drawer) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    abortRequest() {
      this.request?.abort();
      this.request = null;
    }

    parseIndex(value) {
      if (value === undefined || value === null || value === '') return -1;
      const index = Number(value);
      return Number.isInteger(index) ? index : -1;
    }
  }

  if (!customElements.get('mt-fast-quick-buy')) customElements.define('mt-fast-quick-buy', MtFastQuickBuy);

  const triggerDelegateKey = '__mtFastQuickBuyTriggerDelegateReady';
  if (!window[triggerDelegateKey]) {
    window[triggerDelegateKey] = true;
    document.addEventListener(
      'click',
      (event) => {
        const trigger = event.target.closest?.('[data-mt-fast-quick-buy-trigger]');
        const quickBuy = trigger?.closest('mt-fast-quick-buy');
        if (!trigger || !(quickBuy instanceof MtFastQuickBuy) || trigger.disabled) return;
        event.preventDefault();
        event.stopPropagation();
        quickBuy.open(trigger);
      },
      { capture: true }
    );
  }
})();
